/* Mock routes — care schedule. */

import type { CareTask } from '@core/types'
import { ApiError } from '../../http'
import { db, nextId } from '../store'
import { readNumber, type MockRoute } from '../types'
import { addDays, awardPoints, byUrgency } from './shared'

export const careRoutes: MockRoute[] = [
  {
    method: 'GET',
    pattern: '/care-tasks',
    handler: ({ query }) => {
      const status = query.get('status')
      const plantId = query.get('plantId')
      let tasks = [...db.careTasks]
      if (status && status !== 'all') tasks = tasks.filter((task) => task.status === status)
      if (plantId) tasks = tasks.filter((task) => task.plantId === plantId)
      return tasks.sort(byUrgency)
    },
    emptyState: [] satisfies CareTask[],
  },
  {
    method: 'POST',
    pattern: '/care-tasks/:id/complete',
    handler: ({ params }) => {
      const task = db.careTasks.find((item) => item.id === params.id)
      if (!task) {
        throw new ApiError({
          status: 404,
          code: 'task_not_found',
          message: 'That care task no longer exists — it may have been completed on another device.',
        })
      }
      if (task.status === 'completed') {
        throw new ApiError({
          status: 409,
          code: 'task_already_completed',
          message: 'This task is already completed, so no points were added again.',
        })
      }

      task.status = 'completed'
      const points = task.rewardPoints
      awardPoints(points, `Care task completed — ${task.title}`, 'care')
      db.activity.unshift({
        id: nextId('ac', 700),
        kind: 'care-completed',
        title: task.title,
        detail: `${task.plantName} · completed`,
        occurredAt: new Date().toISOString(),
        points,
      })

      // Recurring tasks create the next occurrence, exactly like the real API must.
      if (task.repeatEveryDays) {
        db.careTasks.push({
          ...task,
          id: nextId('ct', 200),
          status: 'upcoming',
          dueAt: addDays(task.repeatEveryDays),
        })
      }

      return { task, pointsAwarded: points, account: db.rewardAccount }
    },
  },
  {
    method: 'POST',
    pattern: '/care-tasks/:id/snooze',
    handler: ({ params, body }) => {
      const task = db.careTasks.find((item) => item.id === params.id)
      if (!task) {
        throw new ApiError({
          status: 404,
          code: 'task_not_found',
          message: 'That care task no longer exists.',
        })
      }
      const days = readNumber(body, 'days') ?? 1
      task.dueAt = addDays(days)
      task.status = 'due'
      return task
    },
  },
]
